import { useState, useRef, useEffect, useCallback } from 'react'
import { PlayIcon, PauseIcon, FullscreenIcon, VideoLoader } from './VideoIcons'
import VideoProgressBar from './VideoProgressBar'

const IDLE_TIMEOUT_MS = 2500

interface VideoControlsProps {
  isPlaying: boolean
  isLoading?: boolean
  progress: number
  onTogglePlay: () => void
  onFullscreen?: () => void
  variant?: 'modal' | 'card'
  currentTime?: number
  duration?: number
  onSeek?: (percent: number) => void
}

const VideoControls = ({
  isPlaying,
  isLoading = false,
  progress,
  onTogglePlay,
  onFullscreen,
  variant = 'card',
  currentTime = 0,
  duration = 0,
  onSeek,
}: VideoControlsProps) => {
  const [isHovering, setIsHovering] = useState(false)
  const [isIdle, setIsIdle] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const startIdleTimer = useCallback(() => {
    clearIdleTimer()
    idleTimerRef.current = setTimeout(() => {
      setIsIdle(true)
    }, IDLE_TIMEOUT_MS)
  }, [clearIdleTimer])

  const handleMouseMove = useCallback(() => {
    if (isIdle) {
      setIsIdle(false)
    }
    startIdleTimer()
  }, [isIdle, startIdleTimer])

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true)
    setIsIdle(false)
    startIdleTimer()
  }, [startIdleTimer])

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false)
    setIsIdle(false)
    clearIdleTimer()
  }, [clearIdleTimer])

  useEffect(() => {
    return clearIdleTimer
  }, [clearIdleTimer])

  // Controls visible when: not playing OR (hovering AND not idle)
  const showControls = !isPlaying || (isHovering && !isIdle)

  if (variant === 'modal') {
    return (
      <div
        className="absolute inset-0"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 backdrop-blur-[2px]">
            <svg className="w-10 h-10 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="white" strokeOpacity="0.2" strokeWidth="2" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        )}

        <div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={onTogglePlay}
        >
          <div className={`transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
            {isPlaying ? (
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <rect x="12" y="10" width="8" height="24" rx="2" fill="white" />
                <rect x="24" y="10" width="8" height="24" rx="2" fill="white" />
              </svg>
            ) : (
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <path d="M15 10L35 22L15 34V10Z" fill="white" />
              </svg>
            )}
          </div>
        </div>

        <div className={`absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          <VideoProgressBar
            variant="modal"
            progress={progress}
            currentTime={currentTime}
            duration={duration}
            onSeek={onSeek}
          />
        </div>
      </div>
    )
  }

  // Card variant - clicks bubble to parent, only fullscreen button has its own handler
  return (
    <div
      className="absolute inset-0"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      {isLoading && isPlaying && <VideoLoader />}

      {isPlaying ? (
        <div className={`absolute inset-0 transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <PauseIcon />
          </div>
          <div className="absolute bottom-2 left-2 right-2 sm:bottom-4 sm:left-4 sm:right-4 flex items-center gap-2 sm:gap-3 pointer-events-none">
            <VideoProgressBar variant="compact" progress={progress} />
            {onFullscreen && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onFullscreen()
                }}
                className="flex items-center justify-center text-white hover:text-white/80 pointer-events-auto"
              >
                <FullscreenIcon className="w-4 sm:w-6" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
          <PlayIcon />
        </div>
      )}
    </div>
  )
}

export default VideoControls
